import { ActionResponse } from './app'
import { themeColors } from '~/styles/themeColors.mjs'
import { PaginationPageSettings } from '~/types/paginatedDataset'

export interface Section {
  id: string,
  productId: string,
  title: string,
  slug: string,
}

export const enum CONTENT_TYPE {
  MARKDOWN = 'markdown',
  HTML = 'html',
  API_URL = 'apiUrl',
  GRAPHQL = 'graphql',
}

export type TableOfContentsItem = ApiContent | DocumentContent | GraphQlContent

export interface ApiContent {
  id: string,
  slug: string,
  title: string,
  content: ApiUrlContent
  swaggerhubApi?: {
    private: boolean
  }
}

export interface DocumentContent {
  id: string,
  slug: string,
  title: string,
  content: DocumentItem,
  children: TableOfContentsItem[]
}

export interface GraphQlContent {
  id: string,
  slug: string,
  title: string,
  content: GraphQlApiContent,
}

export interface TableOfContents {
  page?: PaginationPageSettings,
  items: Array<TableOfContentsItem>
}

export interface ApiUrlContent {
  type: CONTENT_TYPE.API_URL,
  url?: string,
  documentId?: never
}

export interface DocumentItem {
  type: CONTENT_TYPE.MARKDOWN | CONTENT_TYPE.HTML,
  documentId: string,
  url?: never
}

export interface GraphQlApiContent {
  type: CONTENT_TYPE.GRAPHQL,
  url?: string,
  graphqlApiId: string
}

export interface Document {
  id?: string,
  documentId?: string,
  type: CONTENT_TYPE,
  content: string,
  createdAt?: string,
  updatedAt?: string,
  createdBy?: string,
  updatedBy?: string,
  version: number,
}

export interface PublishedSections {
  page?: PaginationPageSettings,
  items: PublishedSectionsItem[],
}

export interface PublishedSectionsItem {
  title: string,
  slug: string,
  tableOfContents: PublishedTableOfContents[],
}

export type PublishedTableOfContents = ApiTocItem | DocumentTocItem | GraphQlTocItem

export interface DocumentTocItem {
  tableOfContentsId: string,
  sourceTemplateTocId?: string,
  type: CONTENT_TYPE.MARKDOWN | CONTENT_TYPE.HTML,
  title: string,
  path: string,
  children: PublishedTableOfContents[],
}

export interface ApiTocItem {
  tableOfContentsId: string,
  sourceTemplateTocId?: string,
  type: CONTENT_TYPE.API_URL,
  title: string,
  url: string,
  path: string,
  operations: Operation[],
  schemas?: Schema[]
}

export interface GraphQlTocItem {
  tableOfContentsId: string,
  type: CONTENT_TYPE.GRAPHQL,
  title: string,
  path: string,
  url?: string,
  graphqlComponents?: GraphQlComponent[]
}

export interface GraphQlComponent {
  title: string,
  items: GraphQlComponentItem[],
}

export interface GraphQlComponentItem {
  title: string,
  id: string,
}

export enum OPERATION_METHOD {
  GET = 'get',
  POST = 'post',
  PUT = 'put',
  DELETE = 'delete',
  PATCH = 'patch',
  SUB = 'sub',
  PUB = 'pub',
}

export const OPERATION_METHOD_COLORS: Record<OPERATION_METHOD, string> = {
  get: themeColors['api-get'],
  patch: themeColors['api-patch'],
  post: themeColors['api-post'],
  put: themeColors['api-put'],
  delete: themeColors['api-delete'],
  pub: themeColors['api-pub'],
  sub: themeColors['api-sub']
}

export const OPERATION_METHOD_COLORS_DARK: Record<OPERATION_METHOD, string> = {
  get: themeColors['api-get-dark'],
  patch: themeColors['api-patch-dark'],
  post: themeColors['api-post-dark'],
  put: themeColors['api-put-dark'],
  delete: themeColors['api-delete-dark'],
  pub: themeColors['api-pub-dark'],
  sub: themeColors['api-sub-dark']
}

export interface Operation {
  method: OPERATION_METHOD,
  path: string,
  tag?: string,
  summary: string,
  operationId: string,
  deprecated?: boolean,
}

export interface Schema {
  name: string,
  title?: string,
}

export interface PublishedStatus {
  updatedAt?: string
}

export interface PublishedDifference {
  added: PublishedDifferenceItem[],
  modified: PublishedDifferenceItem[],
  removed: PublishedDifferenceItem[],
  missing: PublishedDifferenceItem[],
  reordered: boolean
}

export interface PublishedDifferenceItem {
  tableOfContentsId: string,
  path?: string,
  title?: string,
  modifications?: ContentDiffTypeEntry[],
  type?: CONTENT_TYPE
}

export enum ContentDiffType {
  API_CHANGED = 'API_CHANGED',
  TITLE_CHANGED = 'TITLE_CHANGED',
  SLUG_CHANGED = 'SLUG_CHANGED',
  DOCUMENT_CHANGED = 'DOCUMENT_CHANGED',
}

export interface ContentDiffTypeEntry {
  type: ContentDiffType
}

export interface ContentDiff {
  added: boolean,
  modified: boolean,
  modifiedTypes?: ContentDiffTypeEntry[]
  removed: boolean,
  missing: boolean,
}

export interface ApiContentDiff extends ApiContent, ContentDiff { }
export interface DocumentContentDiff extends DocumentContent, ContentDiff {
  children: TableOfContentsItemDiff[]
}

export interface GraphQlContentDiff extends GraphQlContent, ContentDiff { }

export type TableOfContentsItemDiff = ApiContentDiff | DocumentContentDiff | GraphQlContentDiff

export interface NotificationData {
  title: string,
  id: string,
  shubUrl?: string,
  type?: CONTENT_TYPE,
}

export interface NotificationCenterData {
  added?: NotificationData[]
  modified?: NotificationData[]
  inaccessibleAPI?: NotificationData[]
  inaccessibleGraphQL?: NotificationData[]
  apiChanged?: NotificationData[]
  removed?: NotificationData[]
  reordered?: boolean
}

export enum EDIT_CONTENT_TYPE {
  PRODUCT = 'product',
  TEMPLATE = 'template',
}

export interface DuplicatePageActionData extends ActionResponse {
  targetPageId?: string;
}

export interface EditContentLoader<T> {
  tableOfContentsItems: T[];
  publishedDifference: PublishedDifference;
}